import React, { ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';

import {
  displayDate,
  displayTime,
  DateTimeOptions,
} from '@adsk/alloy-react-datetime';
import { useLocale } from '@adsk/alloy-react-locale';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

export type FooterProps = StylableComponent<HTMLDivElement> & {
  user?: { name: ReactNode };
  date?: Date;
  dateOptions?: DateTimeOptions;
};

const Footer = React.forwardRef<HTMLDivElement, FooterProps>(
  ({ user, date, dateOptions, className, style, ...props }, ref) => {
    const { T, locale } = useLocale(({ locale: loc }) =>
      require(`../i18n/${loc}.json`),
    );
    const footerTokens = useTheme().tokens?.component?.activityLog?.footer;
    const text = useMemo(() => {
      const dateString = date
        ? `${T.translate('DATE_PREFIX')} ${displayDate(date, {
            locale,
            format: 'numeric',
            utc: false,
            ...dateOptions,
          })}`
        : '';
      const timeString = date
        ? `${T.translate('TIME_PREFIX')} ${displayTime(date, {
            locale,
            format: 'numeric',
            utc: false,
            ...dateOptions,
          })}`
        : '';

      return `${dateString} ${timeString}`;
    }, [date, T, locale, dateOptions]);
    return (
      <div
        ref={ref}
        className={className}
        css={[footerTokens, { display: 'flex' }, style]}
        {...props}
      >
        {user?.name ? (
          <>
            <OverflowTooltip
              getTargetProps={() => ({ style: { overflow: 'auto' } })}
            >
              {user?.name}
            </OverflowTooltip>
            <span
              style={{
                whiteSpace: 'pre',
              }}
            >
              {' '}
            </span>
          </>
        ) : (
          ''
        )}
        <div style={{ flexShrink: 0 }}>{text}</div>
      </div>
    );
  },
);

Footer.displayName = 'Footer';

Footer.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The date to show the activity for */
  date: PropTypes.instanceOf(Date),
  /** The user to show the activity for */
  user: PropTypes.shape({
    name: PropTypes.node.isRequired,
  }),
};

export default Footer;
